'use client'

import { Button } from '@lastbrand/web-ui/button'
import { useTranslation } from 'next-i18next'
import { useMemo, useState } from 'react'

import {
  getInitialSelectedVariant,
  getSelectedOptionsForProductCard,
} from '@src/components/product-card/product-card.helpers'
import { CardVariant } from '@src/components/product-card/product-card.types'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { ProductCard } from '@src/ds/blocks/product-card'
import { ProductCTAConstants } from '@src/quince-components/src/atoms/productCTA/constants'
import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import useATCVsATBExperiment from '@src/quince-site-common/src/hooks/use-atc-vs-atb-experiment'
import productBuyNowStateManager from '@src/quince-site-common/src/redux/globalStateManager/ProductBuyNowStateManager'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'

import { WishlistGridDs, wishListItemWrapperCss } from './wishlist-grid-ds'

import type { WishlistItemListDSProps } from './wishlist-item-list-ds.types'

const WISHLIST_DISPLAY_NAME = 'Wishlist'
const WISHLIST_TRACKER = 'wishlist'
const TRACK_CATEGORY = 'product-list'

// `isSelfWishlist` drives the DS card's desktop-only delete (trash) button — a viewer looking at their
// own wishlist can remove items via it (parity with the legacy `showDeleteBtn`). The filled-heart toggle
// remains the removal affordance on mobile and on someone else's shared wishlist.
export function WishlistItemListDS({
  items,
  isLoading = false,
  sectionConfig: { tracker },
  isSelfWishlist,
}: WishlistItemListDSProps) {
  const [selectedVariants, setSelectedVariants] = useState<Record<string, CardVariant>>({})

  const reversedWishlistItems = useMemo(() => {
    return items ? [...items].reverse() : []
  }, [items])

  const { t: tCommon } = useTranslation('common')
  const showATB = useATCVsATBExperiment()
  const atcLabel = showATB ? ProductCTAConstants.ADD_TO_BAG : ProductCTAConstants.ADD_TO_CART

  const hasData = reversedWishlistItems.length > 0

  return (
    <WishlistGridDs isLoading={isLoading} hasData={hasData}>
      {reversedWishlistItems.map((productCard, index) => {
        const selectedCardVariant = getInitialSelectedVariant(productCard.product)
        const selectedColor = selectedCardVariant?.displayConfig.value
        const uniqueKey = `${productCard.product.productId}_${selectedColor}`

        return (
          <div
            key={uniqueKey}
            className={wishListItemWrapperCss}
            data-testid={TEST_LOCATORS.WISHLIST_LIST.ITEM(uniqueKey)}
            onClick={() => {
              setFindingMethod[FINDING_METHOD_AGGREGATE.WISHLIST]?.()
            }}
          >
            {/*
             * Prop parity vs the legacy branch (see wishlist-item-list-legacy.tsx):
             * - `maxWidth` / `className` are not implemented by the DS card — sizing comes from the grid.
             * - `showDeleteBtn` / `hideDeleteForOtherColors` drive the DS card's desktop-only trash button,
             *   matching legacy: shown only on the viewer's own wishlist and only for the wishlisted variant.
             */}
            <ProductCard
              productCard={productCard}
              tracker={tracker && `${tracker}__wishlist__${index}`}
              hideBuyNowIcon
              showDeleteBtn={isSelfWishlist}
              hideDeleteForOtherColors
              optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
              shouldShowMore
              productClickTrackConfig={{
                trackInternal: true,
                trackGA: true,
                // @i18n-ignore
                category: TRACK_CATEGORY,
                action: `${tracker ?? WISHLIST_TRACKER}__click_product_wishlist`,
                value: productCard.product.relatedIds.spid.toString(),
                context: {
                  contentContainerType: CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_CARD,
                  contentContainerName: CUSTOM_SECTION_CONTAINER_NAMES.SUBCOLLECTION_DISPLAY,
                  // @i18n-ignore
                  contentContainerDisplayName: WISHLIST_DISPLAY_NAME,
                  findingMethod: FINDING_METHOD_AGGREGATE.WISHLIST,
                },
              }}
              onVariantChange={variant => {
                setSelectedVariants(prev => ({
                  ...prev,
                  [uniqueKey]: variant,
                }))
              }}
            />
            {/* Desktop-only, breakpoint-hidden per DS convention (legacy used a UA device check).
                    Fires no telemetry of its own — parity with the legacy button. */}
            <Button
              variant="tertiary"
              size="lg"
              data-testid={TEST_LOCATORS.WISHLIST_LIST.ADD_TO_CART(uniqueKey)}
              css={{
                display: { base: 'none', lg: 'block' },
                w: 'full',
                mt: 'space-16',
                // Hover adopts the primary variant's resting look (matches the legacy orange hover).
                _hover: { bg: 'cantaloupe-100', color: 'black', boxShadow: 'none' },
              }}
              onClick={() => {
                const currentVariant = selectedVariants[uniqueKey] ?? selectedCardVariant
                productBuyNowStateManager.showBuyNowPopUp({
                  productCard: productCard,
                  selectedOptions: getSelectedOptionsForProductCard(currentVariant),
                })
              }}
            >
              {(selectedVariants[uniqueKey] ?? selectedCardVariant)?.displayConfig.appExclusive
                ? tCommon('shop_in_the_app').toUpperCase()
                : tCommon(atcLabel)}
            </Button>
          </div>
        )
      })}
    </WishlistGridDs>
  )
}
